/**
 * Hook for accessing local page content from YAML files
 *
 * This hook provides access to pages stored in content/pages/*.yaml
 * These pages use an agent-first structure optimized for AI editing.
 */

import { graphql, useStaticQuery } from "gatsby"

/**
 * Helper to parse inline markdown (bold, italic) into rich text nodes
 * Returns an array of text nodes with appropriate marks
 */
function parseInlineMarkdown(text) {
  if (!text) return []

  const nodes = []
  // Match **bold**, *italic*, and ***bold-italic***
  const inlineRegex = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*)/g
  let lastIndex = 0
  let match = inlineRegex.exec(text)

  while (match !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      nodes.push({
        nodeType: "text",
        value: text.slice(lastIndex, match.index),
        marks: [],
        data: {},
      })
    }

    // Determine which group matched and set marks accordingly
    if (match[2]) {
      // ***bold-italic***
      nodes.push({
        nodeType: "text",
        value: match[2],
        marks: [{ type: "bold" }, { type: "italic" }],
        data: {},
      })
    } else if (match[3]) {
      // **bold**
      nodes.push({
        nodeType: "text",
        value: match[3],
        marks: [{ type: "bold" }],
        data: {},
      })
    } else if (match[4]) {
      // *italic*
      nodes.push({
        nodeType: "text",
        value: match[4],
        marks: [{ type: "italic" }],
        data: {},
      })
    }

    lastIndex = match.index + match[0].length
    match = inlineRegex.exec(text)
  }

  // Add remaining text
  if (lastIndex < text.length) {
    nodes.push({
      nodeType: "text",
      value: text.slice(lastIndex),
      marks: [],
      data: {},
    })
  }

  return nodes.length > 0
    ? nodes
    : [{ nodeType: "text", value: text, marks: [], data: {} }]
}

/**
 * Helper to convert markdown text to Contentful rich text format
 * Supports: [links](url), **bold**, *italic*, ***bold-italic***, **[bold links](url)**
 * Used by DemoSection, dark hero descriptions, and column3-cards
 */
export function markdownToRichText(text) {
  if (!text) return null

  const content = []
  // Match both bold-wrapped links **[text](url)** and regular links [text](url)
  const linkRegex = /(\*\*\[([^\]]+)\]\(([^)]+)\)\*\*|\[([^\]]+)\]\(([^)]+)\))/g
  let lastIndex = 0
  let match = linkRegex.exec(text)

  while (match !== null) {
    // Add text before the link (with inline markdown parsing)
    if (match.index > lastIndex) {
      content.push(...parseInlineMarkdown(text.slice(lastIndex, match.index)))
    }

    if (match[2] && match[3]) {
      // Bold-wrapped link: **[text](url)**
      content.push({
        nodeType: "hyperlink",
        data: { uri: match[3] },
        content: [
          {
            nodeType: "text",
            value: match[2],
            marks: [{ type: "bold" }],
            data: {},
          },
        ],
      })
    } else if (match[4] && match[5]) {
      // Regular link: [text](url)
      content.push({
        nodeType: "hyperlink",
        data: { uri: match[5] },
        content: parseInlineMarkdown(match[4]),
      })
    }

    lastIndex = match.index + match[0].length
    match = linkRegex.exec(text)
  }

  // Add remaining text (with inline markdown parsing)
  if (lastIndex < text.length) {
    content.push(...parseInlineMarkdown(text.slice(lastIndex)))
  }

  // If no links were found, just parse inline markdown
  const finalContent = content.length > 0 ? content : parseInlineMarkdown(text)

  return {
    raw: JSON.stringify({
      nodeType: "document",
      data: {},
      content: [
        {
          nodeType: "paragraph",
          data: {},
          content: finalContent,
        },
      ],
    }),
  }
}

/**
 * Get all local pages
 */
function useLocalPages() {
  const data = useStaticQuery(graphql`
    query LocalPagesQuery {
      allPagesYaml {
        nodes {
          title
          slug
          description
          headerDark
          hero {
            label
            heading
            description
            image {
              url
              alt
            }
          }
          sections {
            type
            layout
            label
            heading
            subheading
            description
            features {
              heading
              description
              icon
            }
            cards {
              heading
              description
              logo
              document
              link
            }
            categories {
              title
              questions {
                question
                answer
              }
            }
            button {
              text
              url
              trackingName
            }
          }
        }
      }
    }
  `)

  return data.allPagesYaml.nodes
}

/**
 * Get a specific local page by slug
 */
function _useLocalPage(slug) {
  const pages = useLocalPages()
  return pages.find((page) => page.slug === slug) || null
}

/**
 * Transform local page hero to TwoSideImageAndText format
 */
export function transformHeroToComponent(hero) {
  if (!hero) return null

  return {
    heading: hero.heading,
    subtitle: hero.description
      ? {
          childMarkdownRemark: {
            rawMarkdownBody: hero.description,
          },
        }
      : null,
    image: hero.image
      ? {
          url: hero.image.url,
          description: hero.image.alt,
        }
      : null,
    imageLink: hero.imageLink || null,
    imageSize: hero.imageSize,
    ctaButtons: hero.buttons
      ? hero.buttons.map((btn) => ({
          buttonLabel: btn.text,
          color: btn.color || "green",
          url: btn.url,
          trackingName: btn.trackingName,
          contactModal: btn.contactModal || false,
          contactModalSource: btn.contactModalSource || "",
        }))
      : null,
    heroSection: true,
    decorated: hero.decorated || false,
  }
}

/**
 * Transform feature-grid section to Column2BulletGrid format
 */
export function transformFeatureGridToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    description: section.description,
    bulletItemsLayout:
      section.layout === "horizontal" ? "Horizontal" : "Vertical",
    bulletItems: (section.features || []).map((feature, index) => ({
      id: `feature-${index}`,
      heading: feature.heading,
      description: containsMarkdown(feature.description)
        ? null
        : feature.description,
      richText: containsMarkdown(feature.description)
        ? markdownToRichText(feature.description)
        : null,
      image: feature.icon ? { url: feature.icon } : null,
    })),
    // Section-level background image (appears on the right side)
    image: section.image
      ? { url: section.image.url, title: section.image.alt || "" }
      : null,
  }
}

/**
 * Check if text contains markdown formatting
 */
function containsMarkdown(text) {
  if (!text) return false
  // Check for bold, italic, or links
  return /\*\*|\*[^*]|\[.+\]\(.+\)/.test(text)
}

/**
 * Transform cards section to Column2CardGroup format
 */
export function transformCardsToComponent(section) {
  return {
    layout: section.layout === "secondary" ? "Secondary" : "Primary",
    label: section.label,
    heading: section.heading,
    subHeading: section.subheading,
    boxItems: (section.cards || []).map((card, index) => ({
      id: `card-${index}`,
      heading: card.heading,
      // Use richText for markdown content, plain description otherwise
      description: containsMarkdown(card.description) ? null : card.description,
      richText: containsMarkdown(card.description)
        ? markdownToRichText(card.description)
        : null,
      logo: card.logo ? { url: card.logo } : null,
      document: card.document ? { url: card.document } : null,
      ctaLink: card.link,
    })),
  }
}

/**
 * Transform FAQ section to FAQ component format
 */
export function transformFaqToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    content: {
      sections: (section.categories || []).map((category) => ({
        title: category.title,
        faqs: (category.questions || []).map((q) => ({
          question: q.question,
          answer: q.answerHtml || q.answer,
        })),
      })),
    },
  }
}

/**
 * Transform CTA section to CTASection component format
 */
export function transformCtaToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    description: section.description,
    ctaButton: section.button
      ? {
          buttonLabel: section.button.text,
          url: section.button.url,
          trackingName: section.button.trackingName,
          contactModal: section.button.contactModal || false,
          contactModalSource: section.button.contactModalSource || "",
        }
      : null,
  }
}

/**
 * Transform dark hero section to HeroSection component format
 */
export function transformDarkHeroToComponent(hero) {
  if (!hero) return null

  // Explicit fullHeight from YAML takes precedence, otherwise:
  // full height when there's a video, carousel, or an image without a background
  let fullHeight = hero.fullHeight
  if (fullHeight === undefined) {
    const hasVideo = !!hero.video
    const hasCarousel = !!hero.carousel?.length
    const hasImageWithoutBackground = !!hero.image && !hero.background
    fullHeight = hasVideo || hasCarousel || hasImageWithoutBackground
  }

  return {
    heading: hero.heading,
    richText: markdownToRichText(hero.description),
    heroVideo: hero.video,
    heroCarousel: hero.carousel || null,
    heroImage: hero.image
      ? { url: hero.image.url, description: hero.image.alt }
      : null,
    heroImageLink: hero.imageLink || null,
    backgroundImage: hero.background
      ? { url: hero.background.url, title: hero.background.alt }
      : null,
    animatedBackground: hero.animatedBackground || null,
    fullHeight,
    ctaButtons: (hero.buttons || []).map((btn) => ({
      buttonLabel: btn.text,
      color: btn.color || "green",
      url: btn.url,
      trackingName: btn.trackingName,
      contactModal: btn.contactModal || false,
      contactModalSource: btn.contactModalSource || "",
      newTab: btn.newTab || false,
    })),
    heroForm: hero.formId
      ? {
          formId: hero.formId,
          thankYouMessage: hero.thankYouMessage,
        }
      : null,
  }
}

/**
 * Transform expert-cta section to ApacheSupersetExpertSection format
 */
export function transformExpertCtaToComponent(section) {
  return {
    heading: section.heading,
    description: section.description,
    registrationButtonText: section.primaryButton?.text,
    productPageButtonText: section.secondaryButton?.text,
    bottomText: section.bottomText
      ? {
          childrenMarkdownRemark: [
            {
              html: section.bottomText.replace(
                /\[([^\]]+)\]\(([^)]+)\)/g,
                '<a href="$2">$1</a>'
              ),
            },
          ],
        }
      : null,
  }
}

/**
 * Transform markdown section to PageMarkdown component format
 */
export function transformMarkdownToComponent(section) {
  return {
    heading: section.heading,
    content: section.content
      ? { childMarkdownRemark: { html: section.content, htmlAst: null } }
      : null,
  }
}

/**
 * Transform logo-grid section to DashboardLogo component format
 */
export function transformLogoGridToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    description: section.description,
    logos: (section.logos || []).map((logo, index) => ({
      id: `logo-${index}`,
      url: logo.url,
      title: logo.alt,
    })),
  }
}

/**
 * Transform two-cards section to JoinSection component format
 */
export function transformTwoCardsToComponent(section) {
  return {
    leftText: section.leftCard?.text,
    leftLogo: section.leftCard?.logo ? { url: section.leftCard.logo } : null,
    leftCtaText: section.leftCard?.button?.text,
    leftCtaLink: section.leftCard?.button?.url,
    leftCtaTracking: section.leftCard?.button?.trackingName,
    centerText: section.centerCard?.text,
    centerLogo: section.centerCard?.logo
      ? { url: section.centerCard.logo }
      : null,
    centerCtaText: section.centerCard?.button?.text,
    centerCtaLink: section.centerCard?.button?.url,
    centerCtaTracking: section.centerCard?.button?.trackingName,
    rightText: section.rightCard?.text,
    rightLogo: section.rightCard?.logo ? { url: section.rightCard.logo } : null,
    rightCtaText: section.rightCard?.button?.text,
    rightCtaLink: section.rightCard?.button?.url,
    rightCtaTracking: section.rightCard?.button?.trackingName,
  }
}

/**
 * Transform image-text section to TwoSideImageAndText format
 * Used for responsive image + text layouts with native inverted/flipped support
 */
export function transformImageTextToComponent(section) {
  return {
    heading: section.heading,
    subheading: section.subheading || null,
    subtitle: section.description
      ? {
          childMarkdownRemark: {
            rawMarkdownBody: section.description,
          },
        }
      : null,
    image: section.image
      ? {
          url: section.image.url,
          description: section.image.alt,
        }
      : null,
    imageLink: section.imageLink || null,
    imageShadow: section.imageShadow || false,
    carousel: section.carousel || null,
    ctaButtons: section.buttons
      ? section.buttons.map((btn) => ({
          buttonLabel: btn.text,
          color: btn.color || "green",
          url: btn.url,
          trackingName: btn.trackingName,
          contactModal: btn.contactModal || false,
          contactModalSource: btn.contactModalSource || "",
        }))
      : section.button
        ? [
            {
              buttonLabel: section.button.text,
              color: section.button.color || "green",
              url: section.button.url,
              trackingName: section.button.trackingName,
              contactModal: section.button.contactModal || false,
              contactModalSource: section.button.contactModalSource || "",
            },
          ]
        : null,
    inverted: section.inverted || false,
    flipped: section.flipped || false,
  }
}

/**
 * Transform pricing section to PricingSection component format
 */
export function transformPricingToComponent(section) {
  return {
    title: section.heading,
    description: section.description,
    headingAlignment: section.centered ? "Align Center" : "Align Left",
    pricings: (section.tiers || []).map((tier) => ({
      title: tier.name,
      icon: tier.icon || "",
      description: tier.description,
      price: tier.price,
      priceText: {
        raw: JSON.stringify({
          nodeType: "document",
          data: {},
          content: [
            {
              nodeType: "paragraph",
              data: {},
              content: [
                {
                  nodeType: "text",
                  value: tier.priceSubtext || "",
                  marks: [],
                  data: {},
                },
              ],
            },
          ],
        }),
      },
      priceUnit: tier.priceUnit || "",
      badge: tier.badge || "",
      ctaText: tier.button?.text,
      ctaLink: tier.button?.url,
      ctaTrackingName: tier.button?.trackingName,
      ctaContactModal: tier.button?.contactModal || false,
      ctaContactModalSource: tier.button?.contactModalSource || "",
      highlight: tier.highlighted || false,
      featureListHeading: tier.featuresHeading || "Includes:",
      features: (tier.features || []).map((f) => ({ content: f })),
      customOptionsHeading: tier.optionsHeading,
      customOptions: (tier.options || []).map((o) => ({ content: o })),
      addOnListHeading: tier.addOnsHeading,
      addOnList: (tier.addOns || []).map((a) => ({ content: a })),
      aiHighlight: tier.aiHighlight
        ? {
            heading: tier.aiHighlight.heading,
            items: (tier.aiHighlight.items || []).map((item) => ({
              content: item,
            })),
          }
        : null,
    })),
  }
}

/**
 * Transform contact-form section to ContactSection component format
 *
 * Supports two patterns:
 * 1. access-embedded style: sidebarHeading + sidebarText → h3 + bullet list
 * 2. newsletter style: sidebarHeading alone → bold text, sidebarContactText → paragraphs
 */
export function transformContactFormToComponent(section) {
  // Convert text to paragraphs, preserving line breaks and parsing markdown
  const textToParagraphs = (text) => {
    if (!text) return []
    const paragraphs = text
      .split(/\n\n|\n/)
      .map((p) => p.trim())
      .filter((p) => p)
    return paragraphs.map((p) => ({
      nodeType: "paragraph",
      data: {},
      content: parseInlineMarkdown(p),
    }))
  }

  // Convert bullet list text to rich text unordered list with markdown support.
  // Supports nesting via 2-space indentation per level.
  const parseBulletList = (text) => {
    if (!text) return []
    const bulletLines = text
      .split("\n")
      .filter((line) => line.trim().startsWith("-"))
    if (bulletLines.length === 0) return textToParagraphs(text)

    const makeItem = (line) => ({
      nodeType: "list-item",
      data: {},
      content: [
        {
          nodeType: "paragraph",
          data: {},
          content: parseInlineMarkdown(line.replace(/^-\s*/, "").trim()),
        },
      ],
    })

    const rootList = { nodeType: "unordered-list", data: {}, content: [] }
    const lastItemAtLevel = []

    for (const raw of bulletLines) {
      const indent = raw.length - raw.trimStart().length
      const level = Math.floor(indent / 2)
      const item = makeItem(raw.trimStart())

      let targetList = rootList
      const parentItem = level > 0 ? lastItemAtLevel[level - 1] : null
      if (parentItem) {
        let subList = parentItem.content.find(
          (c) => c.nodeType === "unordered-list"
        )
        if (!subList) {
          subList = { nodeType: "unordered-list", data: {}, content: [] }
          parentItem.content.push(subList)
        }
        targetList = subList
      }
      targetList.content.push(item)
      lastItemAtLevel[level] = item
      lastItemAtLevel.length = level + 1
    }

    return [rootList]
  }

  // Create rich text with h3 heading and content (access-embedded style)
  const toHeadingWithContent = (heading, text) => {
    if (!heading && !text) return null
    const content = []
    if (heading) {
      content.push({
        nodeType: "heading-3",
        data: {},
        content: [{ nodeType: "text", value: heading, marks: [], data: {} }],
      })
    }
    if (text) {
      content.push(...parseBulletList(text))
    }
    return content.length > 0
      ? { raw: JSON.stringify({ nodeType: "document", data: {}, content }) }
      : null
  }

  // Create rich text with bold heading only (newsletter style)
  const toBoldHeadingRichText = (heading) => {
    if (!heading) return null
    return {
      raw: JSON.stringify({
        nodeType: "document",
        data: {},
        content: [
          {
            nodeType: "paragraph",
            data: {},
            content: [
              {
                nodeType: "text",
                value: heading,
                marks: [{ type: "bold" }],
                data: {},
              },
            ],
          },
        ],
      }),
    }
  }

  // Create rich text from paragraphs
  const toParagraphsRichText = (text) => {
    if (!text) return null
    const paragraphs = textToParagraphs(text)
    if (paragraphs.length === 0) return null
    return {
      raw: JSON.stringify({
        nodeType: "document",
        data: {},
        content: paragraphs,
      }),
    }
  }

  // Simple rich text (single paragraph)
  const toRichText = (text) =>
    text
      ? {
          raw: JSON.stringify({
            nodeType: "document",
            data: {},
            content: [
              {
                nodeType: "paragraph",
                data: {},
                content: [
                  { nodeType: "text", value: text, marks: [], data: {} },
                ],
              },
            ],
          }),
        }
      : null

  // Determine which pattern to use based on presence of sidebarText
  // Pattern 1: sidebarText present → h3 + bullet list style (access-embedded)
  // Pattern 2: sidebarText absent → bold heading + paragraphs style (newsletter)
  const hasSidebarText = Boolean(section.sidebarText)

  return {
    label: section.label,
    heading: section.heading,
    headingImage: section.headingImage
      ? { url: section.headingImage.url, description: section.headingImage.alt }
      : null,
    hubspotForm: section.hubspotFormId
      ? {
          formId: section.hubspotFormId,
          trackSubmit: section.trackSubmit,
          submitText: section.submitText,
        }
      : null,
    leftSideRichText: hasSidebarText
      ? toHeadingWithContent(section.sidebarHeading, section.sidebarText)
      : toBoldHeadingRichText(section.sidebarHeading),
    leftSideContactMessage: hasSidebarText
      ? toHeadingWithContent(
          section.sidebarContactHeading,
          section.sidebarContactText
        )
      : toParagraphsRichText(section.sidebarContactText),
    successMessage: toRichText(section.successMessage),
    logosSectionHeading: section.logosHeading,
    logos: (section.logos || []).map((logo, i) => ({
      id: `logo-${i}`,
      url: logo.url,
      title: logo.alt,
      description: logo.alt,
    })),
    requireJobTitle: Boolean(section.requireJobTitle),
  }
}

/**
 * Transform rich-text section to PageRichText component format
 * Used for legal pages with long-form content
 */
export function transformRichTextToComponent(section) {
  // If content is markdown/HTML string, wrap it appropriately
  return {
    richText: section.content
      ? {
          raw: JSON.stringify({
            nodeType: "document",
            data: {},
            content: [
              {
                nodeType: "paragraph",
                data: {},
                content: [
                  {
                    nodeType: "text",
                    value: section.content,
                    marks: [],
                    data: {},
                  },
                ],
              },
            ],
          }),
        }
      : null,
    // Use processed markdown/HTML from GraphQL
    htmlContent: section.contentHtml,
  }
}

/**
 * Transform hubspot-form section to HubspotFormEmbed component format
 * Used for simple embedded HubSpot forms
 * @param {Object} section - The section configuration from YAML
 * @param {Object} [context] - Optional context (e.g., page slug for hidden fields)
 */
export function transformHubspotFormToComponent(section, context = {}) {
  return {
    formId: section.formId,
    trackSubmit: section.trackSubmit,
    thankYouMessage: section.thankYouMessage,
    width: section.width,
    variant: section.formVariant,
    hiddenFields: {
      ...section.hiddenFields,
      ...(context.sourcePage && { source_page: context.sourcePage }),
    },
  }
}

/**
 * Transform bullet-grid section to Column2BulletGrid component format
 * Used for feature lists with bullet points
 */
export function transformBulletGridToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    description: section.description,
    bulletItemsLayout:
      section.layout === "horizontal" ? "Horizontal" : "Vertical",
    bulletItems: (section.bullets || []).map((bullet) => ({
      heading: bullet.heading,
      // Render markdown (links, bold) as rich text; plain strings pass through.
      description: containsMarkdown(bullet.description)
        ? null
        : bullet.description,
      richText: containsMarkdown(bullet.description)
        ? markdownToRichText(bullet.description)
        : null,
      logo: bullet.icon ? { url: bullet.icon } : null,
      link: bullet.link,
    })),
    compact: section.compact || false,
    inverted: section.inverted || false,
    background: section.background
      ? { url: section.background.url, title: section.background.alt }
      : null,
  }
}

/**
 * Transform demo section to DemoSection component format
 * Used for demo pages with recorded video and scheduled live demos
 */
export function transformDemoToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    body: markdownToRichText(section.description),
    videoUrl: section.videoUrl,
    recordedVideoDate: section.recordedVideoDate,
    handsOnLab: section.handsOnLab?.webinarTopic
      ? {
          label: section.handsOnLab.label,
          title: section.handsOnLab.title,
          description: section.handsOnLab.description,
          webinarTopic: section.handsOnLab.webinarTopic,
        }
      : null,
    footer: markdownToRichText(section.footer),
    footerBackgroundImage: section.footerBackground
      ? {
          gatsbyImageData: null,
          url: section.footerBackground,
          description: "Footer background",
        }
      : null,
  }
}

/**
 * Transform support section to SupportSection component format
 * Used for support pages with cards and HubSpot form
 */
export function transformSupportToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    subHeading: section.subheading,
    description: section.description,
    contentItems: (section.items || []).map((item, index) => ({
      id: `support-item-${index}`,
      heading: item.heading,
      description: item.description,
      ctaText: item.ctaText,
      ctaLink: item.ctaLink,
      logo: item.icon
        ? {
            file: { url: item.icon },
          }
        : null,
    })),
    supportFormUrl: section.supportFormUrl,
    hubspotForm: section.hubspotFormId
      ? {
          formId: section.hubspotFormId,
          formLabel: "Submit a support ticket",
          thankYouMessage: section.thankYouMessage,
        }
      : null,
    backgroundImage: section.backgroundImage
      ? {
          gatsbyImageData: null,
          url: section.backgroundImage,
          title: "Background",
        }
      : null,
  }
}

/**
 * Transform comparison-table section to ComparisonTable component format
 * Used for feature comparison tables (e.g., Preset vs competitors)
 */
export function transformComparisonTableToComponent(section) {
  return {
    label: section.label,
    heading: section.heading,
    description: section.description,
    presetColumn: section.presetColumn || "Preset",
    competitorColumn: section.competitorColumn || "Competitor",
    rows: section.rows || [],
  }
}
